import { SecureLockIcon } from '@components/Icons/SecureLockIcon';
import {
  LibraryNameInput,
  LibraryNameInputRef,
} from '@components/LibraryNameInput/LibraryNameInput';
import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import { FOLDER_ROOT_PATH } from '@lib/library/folderPath';
import { formatBytesToString, pluralize } from '@lib/utils/units';
import { getLibraryUrl } from '@lib/utils/url';
import { ActionButton } from '@pages/ActionButton';
import { DEFAULT_LIBRARY_NAME } from '@pages/Libraries/components/LibraryCreateModal';
import { billingStore } from '@stores/Billing';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewLibraryModalBase } from './NewLibraryModalBase';
import css from './NewLibraryModalBase.module.scss';

interface NewLibraryViaFileUploadModalProps {
  open: boolean;
  onClose: () => void;
  files: File[];
}

export const NewLibraryViaFileUploadModal = ({
  open,
  onClose,
  files,
}: NewLibraryViaFileUploadModalProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const libraryNameInputRef = useRef<LibraryNameInputRef>(null);
  const libraryManager = useLibraryManager();
  const uploadManager = useUploadManager();
  const navigate = useNavigate();
  const autoFocusOnDesktop = useDesktopAutoFocus();

  const getLibraryName = () => {
    const value = libraryNameInputRef.current?.getValue() || '';
    return value.trim() || DEFAULT_LIBRARY_NAME;
  };

  const handleConfirmUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      // Create new library
      const newLibrary = await libraryManager.createNewLibrary(getLibraryName());
      if (newLibrary) {
        // Navigate to the new library
        navigate(getLibraryUrl(newLibrary._id, newLibrary.name));
        onClose();

        // Upload files to the new library
        await uploadManager.uploadFiles(files, {
          libraryId: newLibrary._id,
          folderPath: FOLDER_ROOT_PATH,
        });

        // Refetch credits after upload completion
        await billingStore.refetchCreditsAfterOperation();
      }
    } catch (error) {
      console.error('Failed to create library or upload files:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const totalSize = files.reduce((acc, file) => acc + file.size, 0);

  return (
    <NewLibraryModalBase open={open} onClose={onClose}>
      <div className={css.formFieldAndLabel}>
        <label htmlFor="library-name">Library name</label>
        <div className={css.inputContainer}>
          <LibraryNameInput
            ref={libraryNameInputRef}
            id="library-name"
            placeholder={DEFAULT_LIBRARY_NAME}
            autoFocus={autoFocusOnDesktop}
          />
        </div>
      </div>
      <div className={css.formFieldAndLabel}>
        <label htmlFor="selected-files">Selected files</label>
        <div className={css.selectedChannelContainer}>
          <SecureLockIcon />
          <input
            type="text"
            id="selected-files"
            value={`${pluralize(files.length, 'file')} selected`}
            disabled={true}
            className={css.selectedChannelInput}
            readOnly
          />
        </div>
        <div className={css.videoInfo}>
          {pluralize(files.length, 'file')} • {formatBytesToString(totalSize)} total size
        </div>
      </div>

      <div className={css.actionButtons}>
        <ActionButton variant="secondary" onClick={onClose} disabled={isUploading}>
          Cancel
        </ActionButton>
        <ActionButton onClick={handleConfirmUpload} disabled={isUploading || files.length === 0}>
          {isUploading ? 'Creating library...' : 'Create library and upload'}
        </ActionButton>
      </div>
    </NewLibraryModalBase>
  );
};
